import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectTokenUserId } from "../../store/auth-slice";
import {
  selectAssociations,
  selectSelectedAssociationId,
} from "../../store/associationContext-slice";
import { chooseAssociation } from "../../store/associationContext-actions";
import styles from "./AssociationSwitcher.module.scss";

// Association portal: which administered association the pages show. Renders
// nothing for the common single-association account. Changing it only changes
// the selection; every page re-derives from `selectSelectedAssociation`.

export interface AssociationSwitcherProps {
  /**
   * Asked before the change is applied; return false to keep the current
   * selection (a page with unsaved edits confirms first). The select is
   * controlled, so a refused change snaps back.
   */
  onBeforeChange?: () => boolean;
  /**
   * A write is in flight on the page: no switch until it settles, so its
   * answer never lands on a different association (plan 4.1).
   */
  disabled?: boolean;
}

export const SWITCHER_LABEL = "Izbor udruženja";

const AssociationSwitcher: React.FC<AssociationSwitcherProps> = ({
  onBeforeChange,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const userId = useSelector(selectTokenUserId);
  const associations = useSelector(selectAssociations);
  const selectedId = useSelector(selectSelectedAssociationId);

  if (associations.length < 2 || userId == null) {
    return null;
  }

  return (
    <label className={styles.switcher}>
      <span className={styles.label}>Udruženje</span>
      <select
        className={styles.select}
        aria-label={SWITCHER_LABEL}
        value={selectedId ?? ""}
        disabled={disabled}
        onChange={(event) => {
          const id = Number(event.target.value);
          if (!Number.isFinite(id) || id === selectedId) {
            return;
          }
          if (onBeforeChange && !onBeforeChange()) {
            return;
          }
          dispatch<any>(chooseAssociation(userId, id));
        }}
      >
        {associations.map((association) => (
          <option key={association.id} value={association.id}>
            {association.city
              ? `${association.name} (${association.city})`
              : association.name}
          </option>
        ))}
      </select>
    </label>
  );
};

export default AssociationSwitcher;
